import * as React from 'react';
import { createContext, ReactNode, useContext } from 'react';

import { Message as AIMessage, useChat } from '@ai-sdk/react';

import { useAiApiHost } from '../StudioApp/hooks/useAiApiHost';

interface StudyBuilderContextType {
  messages: AIMessage[];
  input: string;
  handleInputChange: (value: string) => void;
  handleSubmit: () => void;
  sendMessage: (message: string) => void;
  isLoading: boolean;
}

const StudyBuilderContext = createContext<StudyBuilderContextType | undefined>(undefined);

interface StudyBuilderProviderProps {
  children: ReactNode;
}

export const StudyBuilderProvider: React.FC<StudyBuilderProviderProps> = ({ children }) => {
  const aiApiHost = useAiApiHost();
  const { messages, input, handleInputChange, handleSubmit, append, isLoading } = useChat({
    api: `${aiApiHost}/api/ai/v1/chat`,
    credentials: 'include',
    streamProtocol: 'data'
  });

  const handleInputChangeWrapper = (value: string) => {
    const syntheticEvent = {
      target: { value },
      currentTarget: { value }
    } as React.ChangeEvent<HTMLTextAreaElement>;
    handleInputChange(syntheticEvent);
  };

  const sendMessage = (message: string) => {
    append({ role: 'user', content: message });
  };

  const value: StudyBuilderContextType = {
    messages,
    input,
    handleInputChange: handleInputChangeWrapper,
    handleSubmit,
    sendMessage,
    isLoading
  };

  return <StudyBuilderContext.Provider value={value}>{children}</StudyBuilderContext.Provider>;
};

export const useStudyBuilder = (): StudyBuilderContextType => {
  const context = useContext(StudyBuilderContext);
  if (context === undefined) {
    throw new Error('useStudyBuilder must be used within a StudyBuilderProvider');
  }
  return context;
};
